import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Coins, Plus, ArrowLeft, Trash2, Check, X, Loader2, Send, Share2,
  ChevronDown, ChevronUp, Search, User, Clock,
  Play, Square, Edit3, Users, Trophy, SplitSquareVertical,
  CalendarDays, MoreVertical, Repeat2, Wallet,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrProfile } from '../hooks/useNostrQuery';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useTipWallet } from '../hooks/useTipWallet';
import { getBCHPrice } from '../hooks/useBCHPrice';
import { prefetchBCHAddress, getCachedBCHAddress } from '../hooks/useBCHAddress';
import {
  type TipSession, type TipEntry, type SessionTemplate, type ParticipantSummary,
  loadSessions, createSession, updateSession, deleteSession, duplicateSession,
  addTip, removeTip, markTipSent, markTipFailed,
  getParticipantSummaries, getSessionTotals, generateSummaryText,
  groupTipsByDay, formatSats, timeAgoShort,
} from '../lib/tipManager';

// ── Profile search via Primal cache ──────────────────────────────
async function searchProfiles(query: string): Promise<any[]> {
  return new Promise((resolve) => {
    const results: any[] = [];
    const subId = 'tms_' + Math.random().toString(36).slice(2, 8);
    let ws: WebSocket;
    let done = false;
    const finish = () => { if (done) return; done = true; resolve(results); try { ws.close(); } catch {} };

    try {
      ws = new WebSocket('wss://cache2.primal.net/v1');
      ws.onopen = () => {
        ws.send(JSON.stringify(['REQ', subId, { cache: ['user_search', { query, limit: 12 }] }]));
      };
      ws.onmessage = (msg) => {
        try {
          const data = JSON.parse(msg.data);
          if (Array.isArray(data) && data[0] === 'EVENT' && data[1] === subId) {
            const ev = data[2];
            if (ev?.kind === 0) {
              try {
                const content = JSON.parse(ev.content);
                results.push({ pubkey: ev.pubkey, ...content });
              } catch {}
            }
          } else if (Array.isArray(data) && data[0] === 'EOSE') finish();
        } catch {}
      };
      ws.onerror = () => finish();
      ws.onclose = () => { if (!done) finish(); };
      setTimeout(finish, 6000);
    } catch { resolve([]); }
  });
}

// ── Mini profile display ─────────────────────────────────────────
function ProfileChip({ pubkey, size = 'sm' }: { pubkey: string; size?: 'sm' | 'md' }) {
  const { profile } = useNostrProfile(pubkey);
  const name = profile?.display_name || profile?.name || pubkey.slice(0, 8) + '...';
  const imgSize = size === 'md' ? 'w-8 h-8' : 'w-6 h-6';
  const textSize = size === 'md' ? 'text-sm' : 'text-xs';

  return (
    <div className="flex items-center gap-2 min-w-0">
      {profile?.picture ? (
        <img src={profile.picture} alt="" className={`${imgSize} rounded-full object-cover shrink-0`} />
      ) : (
        <div className={`${imgSize} rounded-full bg-primary/10 flex items-center justify-center shrink-0`}>
          <User className="w-3 h-3 text-primary/60" />
        </div>
      )}
      <span className={`${textSize} font-medium truncate`}>{name}</span>
    </div>
  );
}

// ── Add Participant Search Modal ─────────────────────────────────
function AddParticipantModal({
  onSelect,
  onClose,
  existingPubkeys,
}: {
  onSelect: (pubkey: string) => void;
  onClose: () => void;
  existingPubkeys: Set<string>;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim() || query.length < 2) { setResults([]); return; }

    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const profiles = await searchProfiles(query.trim());
        setResults(profiles);
      } catch {}
      setSearching(false);
    }, 300);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <Search className="w-4 h-4 text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by name or username..."
            className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-muted-foreground"
          />
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {searching && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {!searching && results.length === 0 && query.length >= 2 && (
            <div className="text-center py-8 text-sm text-muted-foreground">No users found</div>
          )}
          {!searching && results.map((profile) => {
            const already = existingPubkeys.has(profile.pubkey);
            return (
              <button
                key={profile.pubkey}
                onClick={() => { if (!already) onSelect(profile.pubkey); }}
                disabled={already}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                  already ? 'opacity-40 cursor-not-allowed' : 'hover:bg-muted'
                }`}
              >
                {profile.picture ? (
                  <img src={profile.picture} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <User className="w-4 h-4 text-primary/60" />
                  </div>
                )}
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium truncate">{profile.display_name || profile.name || 'Anonymous'}</p>
                  {profile.nip05 && <p className="text-xs text-muted-foreground truncate">{profile.nip05}</p>}
                </div>
                {already && <span className="text-[10px] text-muted-foreground">Already added</span>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Add Tip Modal ────────────────────────────────────────────────
function AddTipModal({
  sessionId,
  recipientPubkey,
  onDone,
  onClose,
}: {
  sessionId: string;
  recipientPubkey?: string;
  onDone: () => void;
  onClose: () => void;
}) {
  const [selectedPubkey, setSelectedPubkey] = useState(recipientPubkey || '');
  const [showSearch, setShowSearch] = useState(!recipientPubkey);
  const [amountStr, setAmountStr] = useState('');
  const [reason, setReason] = useState('');
  const [isUsd, setIsUsd] = useState(true);

  const price = getBCHPrice();
  const parsedAmount = parseFloat(amountStr);
  const isValid = !isNaN(parsedAmount) && parsedAmount > 0 && selectedPubkey;

  const amountSats = isValid
    ? isUsd
      ? Math.round((parsedAmount / (price || 1)) * 1e8)
      : parsedAmount
    : 0;

  const amountUsd = isValid
    ? isUsd
      ? parsedAmount
      : price ? (parsedAmount / 1e8) * price : 0
    : 0;

  const handleAdd = () => {
    if (!isValid) return;
    addTip(sessionId, selectedPubkey, Math.round(amountSats), amountUsd, reason.trim());
    onDone();
    onClose();
  };

  if (showSearch) {
    return (
      <AddParticipantModal
        existingPubkeys={new Set()}
        onSelect={(pk) => { setSelectedPubkey(pk); setShowSearch(false); }}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="font-bold text-sm">Add Tip</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* Recipient */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Recipient</label>
            {selectedPubkey ? (
              <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-muted">
                <ProfileChip pubkey={selectedPubkey} size="md" />
                {!recipientPubkey && (
                  <button onClick={() => setShowSearch(true)} className="text-xs text-primary hover:underline">Change</button>
                )}
              </div>
            ) : (
              <button onClick={() => setShowSearch(true)} className="w-full px-3 py-2.5 rounded-xl border border-dashed border-border text-sm text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors">
                + Select recipient
              </button>
            )}
          </div>

          {/* Amount */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Amount</label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-bold">
                  {isUsd ? '$' : ''}
                </span>
                <input
                  type="number"
                  step={isUsd ? '0.01' : '1'}
                  min="0"
                  value={amountStr}
                  onChange={e => setAmountStr(e.target.value)}
                  placeholder={isUsd ? '0.00' : '0'}
                  className="w-full bg-muted rounded-xl px-3 py-2.5 text-sm font-mono border border-border focus:outline-none focus:ring-2 focus:ring-primary pl-7 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
              <button
                onClick={() => setIsUsd(!isUsd)}
                className="px-3 py-2.5 rounded-xl border border-border text-xs font-medium hover:bg-muted transition-colors shrink-0"
              >
                {isUsd ? 'USD' : 'Sats'}
              </button>
            </div>
            {isValid && (
              <p className="text-[10px] text-muted-foreground mt-1">
                {isUsd
                  ? `~ ${amountSats.toLocaleString()} sats`
                  : price ? `~ $${amountUsd.toFixed(2)}` : ''}
              </p>
            )}
          </div>

          {/* Reason */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Reason <span className="text-muted-foreground/50">(optional)</span></label>
            <input
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Won round 3, Best answer, etc."
              maxLength={200}
              className="w-full bg-muted rounded-xl px-3 py-2.5 text-sm border border-border focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Submit */}
          <button
            onClick={handleAdd}
            disabled={!isValid}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 disabled:opacity-40 transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Tip
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Participant Row ──────────────────────────────────────────────
function ParticipantRow({
  summary,
  sessionId,
  onRefresh,
  onAddTip,
}: {
  summary: ParticipantSummary;
  sessionId: string;
  onRefresh: () => void;
  onAddTip: (pubkey: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors"
      >
        <ProfileChip pubkey={summary.pubkey} size="md" />
        <div className="flex-1 min-w-0 text-right">
          <p className="text-sm font-bold font-mono text-primary">{formatSats(summary.totalSats)}</p>
          <p className="text-[10px] text-muted-foreground">
            {summary.tipCount} tip{summary.tipCount !== 1 ? 's' : ''}
            {summary.pendingSats > 0 && (
              <span className="text-amber-500 ml-1">({formatSats(summary.pendingSats)} pending)</span>
            )}
          </p>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
      </button>

      {expanded && (
        <div className="border-t border-border">
          <div className="divide-y divide-border">
            {summary.tips.map((tip) => (
              <div key={tip.id} className="flex items-center gap-3 px-4 py-2 text-xs">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold">{formatSats(tip.amountSats)}</span>
                    {tip.reason && <span className="text-muted-foreground truncate">"{tip.reason}"</span>}
                  </div>
                  <span className="text-[10px] text-muted-foreground">{timeAgoShort(tip.createdAt)}</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {tip.sent ? (
                    <span className="flex items-center gap-1 text-emerald-500 text-[10px] font-medium">
                      <Check className="w-3 h-3" /> Sent
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-amber-500 text-[10px] font-medium">
                      <Clock className="w-3 h-3" /> Pending
                    </span>
                  )}
                  {!tip.sent && (
                    <button
                      onClick={() => { removeTip(sessionId, tip.id); onRefresh(); }}
                      className="p-1 rounded hover:bg-red-500/10 text-muted-foreground hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="px-4 py-2 bg-muted/30">
            <button
              onClick={() => onAddTip(summary.pubkey)}
              className="flex items-center gap-1.5 text-xs text-primary font-medium hover:underline"
            >
              <Plus className="w-3 h-3" /> Add another tip
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Session Detail View ──────────────────────────────────────────
function SessionDetail({
  session: initialSession,
  onBack,
  onRefreshList,
}: {
  session: TipSession;
  onBack: () => void;
  onRefreshList: () => void;
}) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const tipWallet = useTipWallet();
  const [session, setSession] = useState(initialSession);
  const [showAddTip, setShowAddTip] = useState(false);
  const [addTipPubkey, setAddTipPubkey] = useState<string | undefined>();
  const [showSearch, setShowSearch] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingTipId, setSendingTipId] = useState<string | null>(null);
  const [sendProgress, setSendProgress] = useState({ sent: 0, total: 0, failed: 0 });
  const [showSummary, setShowSummary] = useState(false);
  const [summaryPosted, setSummaryPosted] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const navigate = useNavigate();

  const refresh = useCallback(() => {
    const sessions = loadSessions();
    const updated = sessions.find(s => s.id === session.id);
    if (updated) setSession({ ...updated });
    onRefreshList();
  }, [session.id, onRefreshList]);

  const summaries = useMemo(() => getParticipantSummaries(session), [session]);
  const totals = useMemo(() => getSessionTotals(session), [session]);
  const existingPubkeys = useMemo(() => new Set(session.tips.map(t => t.recipientPubkey)), [session]);

  // Init wallet on mount
  useEffect(() => {
    tipWallet.init().catch(() => {});
  }, []);

  // ── Send All pending tips ──────────────────────────────────────
  const handleSendAll = async () => {
    if (!tipWallet.ready) {
      await tipWallet.init();
    }

    const pendingTips = session.tips.filter(t => !t.sent);
    if (pendingTips.length === 0) return;

    setSending(true);
    setSendProgress({ sent: 0, total: pendingTips.length, failed: 0 });

    let sentCount = 0;
    let failedCount = 0;

    for (const tip of pendingTips) {
      setSendingTipId(tip.id);
      try {
        // Get BCH address
        let address = getCachedBCHAddress(tip.recipientPubkey);
        if (!address) address = await prefetchBCHAddress(tip.recipientPubkey);
        if (!address) {
          markTipFailed(session.id, tip.id);
          failedCount++;
          setSendProgress({ sent: sentCount, total: pendingTips.length, failed: failedCount });
          continue;
        }

        const txid = await tipWallet.sendTipWithFee(address, BigInt(tip.amountSats));
        markTipSent(session.id, tip.id, txid);
        sentCount++;

        // Small delay between sends to avoid mempool conflicts
        await new Promise(r => setTimeout(r, 2000));
      } catch (err) {
        console.error('Tip send failed:', err);
        markTipFailed(session.id, tip.id);
        failedCount++;
      }
      setSendProgress({ sent: sentCount, total: pendingTips.length, failed: failedCount });
    }

    setSending(false);
    setSendingTipId(null);
    refresh();
  };

  // ── Send single tip ────────────────────────────────────────────
  const handleSendOne = async (tipId: string) => {
    if (!tipWallet.ready) await tipWallet.init();

    const tip = session.tips.find(t => t.id === tipId);
    if (!tip || tip.sent) return;

    setSendingTipId(tipId);
    try {
      let address = getCachedBCHAddress(tip.recipientPubkey);
      if (!address) address = await prefetchBCHAddress(tip.recipientPubkey);
      if (!address) throw new Error('No BCH address');

      const txid = await tipWallet.sendTipWithFee(address, BigInt(tip.amountSats));
      markTipSent(session.id, tip.id, txid);
    } catch (err) {
      console.error('Tip send failed:', err);
      markTipFailed(session.id, tip.id);
    }
    setSendingTipId(null);
    refresh();
  };

  // ── Close session ──────────────────────────────────────────────
  const handleClose = () => {
    updateSession(session.id, { status: 'closed', closedAt: Math.floor(Date.now() / 1000) });
    refresh();
  };

  const handleReopen = () => {
    updateSession(session.id, { status: 'active', closedAt: undefined });
    refresh();
  };

  // ── Post summary to Nostr ──────────────────────────────────────
  const handlePostSummary = async () => {
    if (!user) return;
    const profileMap = new Map<string, any>();
    // We won't have all profiles loaded, but generateSummaryText handles missing ones
    const text = generateSummaryText(session, summaries, profileMap);
    try {
      await publish({
        kind: 1,
        content: text,
        tags: [
          ['t', 'bch'], ['t', 'bitcoincash'], ['t', 'bchnostr'],
          ...summaries.map(s => ['p', s.pubkey]),
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setSummaryPosted(true);
      setTimeout(() => setSummaryPosted(false), 3000);
    } catch (err) {
      console.error('Post summary failed:', err);
    }
  };

  // ── Duplicate ──────────────────────────────────────────────────
  const handleDuplicate = () => {
    const newSession = duplicateSession(session.id);
    if (newSession) {
      setSession(newSession);
      onRefreshList();
    }
    setShowMenu(false);
  };

  // ── Delete ─────────────────────────────────────────────────────
  const handleDelete = () => {
    deleteSession(session.id);
    onRefreshList();
    onBack();
  };

  const isActive = session.status === 'active';

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={onBack} className="p-1 rounded-lg hover:bg-muted -ml-1 shrink-0"><ArrowLeft className="w-5 h-5" /></button>
            <div className="min-w-0">
              <h1 className="text-base font-bold truncate">{session.name}</h1>
              <p className="text-[10px] text-muted-foreground">
                {isActive ? (
                  <span className="text-emerald-500 font-medium">Active</span>
                ) : (
                  <span className="text-muted-foreground">Closed</span>
                )}
                {' '}· {totals.participantCount} participant{totals.participantCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="relative">
            <button onClick={() => setShowMenu(!showMenu)} className="p-2 rounded-lg hover:bg-muted transition-colors">
              <MoreVertical className="w-4 h-4" />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-full mt-1 z-50 bg-card border border-border rounded-xl shadow-xl w-48 py-1">
                  {isActive ? (
                    <button onClick={() => { handleClose(); setShowMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted transition-colors">
                      <Square className="w-3.5 h-3.5" /> Close Session
                    </button>
                  ) : (
                    <button onClick={() => { handleReopen(); setShowMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted transition-colors">
                      <Play className="w-3.5 h-3.5" /> Reopen Session
                    </button>
                  )}
                  <button onClick={handleDuplicate} className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted transition-colors">
                    <Repeat2 className="w-3.5 h-3.5" /> Duplicate for Next Week
                  </button>
                  <button onClick={handlePostSummary} className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted transition-colors">
                    <Share2 className="w-3.5 h-3.5" /> Post Summary to Nostr
                  </button>
                  <div className="my-1 border-t border-border" />
                  <button onClick={() => { setShowDeleteConfirm(true); setShowMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-500 hover:bg-red-500/5 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" /> Delete Session
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4 max-w-lg mx-auto">
        {/* Summary Card */}
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-lg font-bold font-mono text-primary">{formatSats(totals.totalSats)}</p>
              <p className="text-[10px] text-muted-foreground">Total</p>
            </div>
            <div>
              <p className="text-lg font-bold font-mono text-amber-500">{formatSats(totals.pendingSats)}</p>
              <p className="text-[10px] text-muted-foreground">Pending</p>
            </div>
            <div>
              <p className="text-lg font-bold font-mono text-emerald-500">{formatSats(totals.sentSats)}</p>
              <p className="text-[10px] text-muted-foreground">Sent</p>
            </div>
          </div>
        </div>

        {/* Wallet Balance */}
        <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-muted/50 border border-border">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Wallet className="w-3.5 h-3.5" /> Wallet Balance
          </div>
          <span className="text-xs font-mono font-bold">
            {tipWallet.loading ? '...' : `${Number(tipWallet.balanceSats).toLocaleString()} sats`}
          </span>
        </div>

        {/* Send All Button */}
        {totals.pendingCount > 0 && isActive && (
          <button
            onClick={handleSendAll}
            disabled={sending || !tipWallet.ready}
            className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600 disabled:opacity-60 transition-colors"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending {sendProgress.sent}/{sendProgress.total}...
                {sendProgress.failed > 0 && <span className="text-red-200">({sendProgress.failed} failed)</span>}
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send All ({totals.pendingCount} tips · {formatSats(totals.pendingSats)})
              </>
            )}
          </button>
        )}

        {/* Summary posted toast */}
        {summaryPosted && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-sm text-emerald-500 font-medium">
            <Check className="w-4 h-4" /> Summary posted to Nostr!
          </div>
        )}

        {/* Participants */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" /> Leaderboard
            </h3>
            {isActive && (
              <button
                onClick={() => { setAddTipPubkey(undefined); setShowAddTip(true); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Add Tip
              </button>
            )}
          </div>

          {summaries.length === 0 ? (
            <div className="text-center py-12 space-y-3">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-muted flex items-center justify-center">
                <Users className="w-8 h-8 text-muted-foreground/40" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">No tips yet</p>
                <p className="text-xs text-muted-foreground/60">Tap "Add Tip" to start tracking tips for participants</p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {summaries.map((s, i) => (
                <div key={s.pubkey} className="relative">
                  {i < 3 && (
                    <div className="absolute -left-1 -top-1 z-10 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shadow-sm"
                      style={{
                        background: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : '#CD7F32',
                        color: i === 0 ? '#000' : '#fff',
                      }}
                    >
                      {i + 1}
                    </div>
                  )}
                  <ParticipantRow
                    summary={s}
                    sessionId={session.id}
                    onRefresh={refresh}
                    onAddTip={(pk) => { setAddTipPubkey(pk); setShowAddTip(true); }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Delete confirm */}
        {showDeleteConfirm && (
          <div className="rounded-2xl border-2 border-red-500/30 bg-red-500/5 p-4 space-y-3">
            <p className="text-sm text-center font-medium">Delete this session?</p>
            <p className="text-[11px] text-center text-muted-foreground">This cannot be undone. All tip records will be lost.</p>
            <div className="flex gap-2">
              <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-muted transition-colors">Cancel</button>
              <button onClick={handleDelete} className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-colors">Delete</button>
            </div>
          </div>
        )}
      </div>

      {/* Add Tip Modal */}
      {showAddTip && (
        <AddTipModal
          sessionId={session.id}
          recipientPubkey={addTipPubkey}
          onDone={refresh}
          onClose={() => setShowAddTip(false)}
        />
      )}
    </div>
  );
}

// ── Create Session Modal ─────────────────────────────────────────
function CreateSessionModal({
  onCreated,
  onClose,
}: {
  onCreated: (session: TipSession) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [template, setTemplate] = useState<SessionTemplate>('custom');

  const handleCreate = () => {
    if (!name.trim()) return;
    const session = createSession(name.trim(), template);
    onCreated(session);
  };

  const templates: { id: SessionTemplate; label: string; desc: string; icon: React.ReactNode }[] = [
    { id: 'custom', label: 'Custom', desc: 'Add tips manually as the event goes', icon: <Edit3 className="w-4 h-4" /> },
    { id: 'equal-split', label: 'Equal Split', desc: 'Divide a total pot evenly', icon: <SplitSquareVertical className="w-4 h-4" /> },
    { id: 'ranked', label: 'Ranked', desc: 'Prizes for 1st, 2nd, 3rd...', icon: <Trophy className="w-4 h-4" /> },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="font-bold text-sm">New Session</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Session Name</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="BCH Trivia Night - Week of July 14"
              className="w-full bg-muted rounded-xl px-4 py-3 text-sm border border-border focus:outline-none focus:ring-2 focus:ring-primary"
              autoFocus
            />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Template</label>
            <div className="space-y-2">
              {templates.map(t => (
                <button
                  key={t.id}
                  onClick={() => setTemplate(t.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-colors ${
                    template === t.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/30'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    template === t.id ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                  }`}>
                    {t.icon}
                  </div>
                  <div className="text-left">
                    <p className="text-xs font-bold">{t.label}</p>
                    <p className="text-[10px] text-muted-foreground">{t.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleCreate}
            disabled={!name.trim()}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 disabled:opacity-40 transition-colors"
          >
            <Plus className="w-4 h-4" /> Create Session
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Session List Card ────────────────────────────────────────────
function SessionCard({
  session,
  onClick,
}: {
  session: TipSession;
  onClick: () => void;
}) {
  const totals = getSessionTotals(session);
  const isActive = session.status === 'active';

  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/40 transition-all hover:shadow-md group"
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              {isActive && (
                <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 text-[9px] font-bold">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Active
                </span>
              )}
              {!isActive && (
                <span className="px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground text-[9px] font-medium">Closed</span>
              )}
            </div>
            <h3 className="font-bold text-sm truncate group-hover:text-primary transition-colors">{session.name}</h3>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {timeAgoShort(session.createdAt)} · {totals.participantCount} participant{totals.participantCount !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-sm font-bold font-mono text-primary">{formatSats(totals.totalSats)}</p>
            {totals.pendingCount > 0 && (
              <p className="text-[10px] text-amber-500">{totals.pendingCount} pending</p>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════
//  MAIN PAGE
// ══════════════════════════════════════════════════════════════════
export default function TipManagerPage() {
  const { user } = useAppContext();
  const [sessions, setSessions] = useState<TipSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<TipSession | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const refreshSessions = useCallback(() => {
    setSessions(loadSessions());
  }, []);

  useEffect(() => {
    refreshSessions();
  }, [refreshSessions]);

  const activeSessions = useMemo(() => sessions.filter(s => s.status === 'active'), [sessions]);
  const closedSessions = useMemo(() => sessions.filter(s => s.status === 'closed'), [sessions]);

  // ── Not logged in ──────────────────────────────────────────────
  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Coins className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-lg font-bold">Tip Manager</h2>
          <p className="text-sm text-muted-foreground max-w-sm">Log in to create and manage tip sessions for your community activities.</p>
        </div>
      </div>
    );
  }

  // ── Session Detail View ────────────────────────────────────────
  if (selectedSession) {
    return (
      <SessionDetail
        session={selectedSession}
        onBack={() => { setSelectedSession(null); refreshSessions(); }}
        onRefreshList={refreshSessions}
      />
    );
  }

  // ── Session List View ──────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Coins className="w-5 h-5 text-primary" />
            Tip Manager
          </h1>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-4 h-4" /> New Session
          </button>
        </div>
      </div>

      <div className="p-4 space-y-6 max-w-lg mx-auto">
        {/* Active Sessions */}
        {activeSessions.length > 0 && (
          <section>
            <h2 className="text-xs font-bold text-emerald-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500" /> Active Sessions
            </h2>
            <div className="space-y-3">
              {activeSessions.map(s => (
                <SessionCard key={s.id} session={s} onClick={() => setSelectedSession(s)} />
              ))}
            </div>
          </section>
        )}

        {/* Closed Sessions */}
        {closedSessions.length > 0 && (
          <section>
            <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">
              Past Sessions
            </h2>
            <div className="space-y-3">
              {closedSessions.map(s => (
                <SessionCard key={s.id} session={s} onClick={() => setSelectedSession(s)} />
              ))}
            </div>
          </section>
        )}

        {/* Empty State */}
        {sessions.length === 0 && (
          <div className="text-center py-20 space-y-4">
            <div className="mx-auto w-20 h-20 rounded-2xl bg-muted flex items-center justify-center">
              <Coins className="w-10 h-10 text-muted-foreground/40" />
            </div>
            <div className="space-y-2">
              <h2 className="text-lg font-bold text-muted-foreground">No tip sessions yet</h2>
              <p className="text-sm text-muted-foreground/60 max-w-sm mx-auto">
                Create a session to start tracking tips for your community activities, game nights, or events.
              </p>
            </div>
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" /> Create First Session
            </button>
          </div>
        )}

        {/* How It Works */}
        <div className="rounded-2xl border border-border bg-card p-5 space-y-3">
          <h3 className="font-bold text-sm flex items-center gap-2">
            <Coins className="w-4 h-4 text-primary" /> How Tip Manager Works
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <CalendarDays className="w-4 h-4 text-primary" />
              </div>
              <p className="text-xs font-semibold">1. Create a Session</p>
              <p className="text-[10px] text-muted-foreground">Name it after your event (e.g. "BCH Trivia Week of July 14"). Sessions stay open for as long as you need.</p>
            </div>
            <div className="space-y-1.5">
              <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Trophy className="w-4 h-4 text-amber-500" />
              </div>
              <p className="text-xs font-semibold">2. Track Tips</p>
              <p className="text-[10px] text-muted-foreground">Add tips as participants earn them throughout the week. See who's earned what with the live leaderboard.</p>
            </div>
            <div className="space-y-1.5">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Send className="w-4 h-4 text-emerald-500" />
              </div>
              <p className="text-xs font-semibold">3. Send &amp; Share</p>
              <p className="text-[10px] text-muted-foreground">Send all tips at once or one by one. Post the final leaderboard to Nostr so everyone sees the results!</p>
            </div>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <CreateSessionModal
          onCreated={(session) => {
            setShowCreate(false);
            refreshSessions();
            setSelectedSession(session);
          }}
          onClose={() => setShowCreate(false)}
        />
      )}
    </div>
  );
}

